<template>
  <v-container fluid class="pa-2">

    <!-- KPIs + Gràfiques -->
    <v-row class="mt-2">

      <!-- CARD CAMPAÑAS -->
      <v-col cols="12" md="6">
        <v-card class="campaign-card">
          <div class="card-header">
            <div class="left">
              <div class="title">CAMPAÑAS</div>
            </div>

            <div class="right">
              <div class="total-line">
                <span class="total-label">TOTAL CAMPAÑAS</span>
                <span class="total-value">{{ kpis[0].value }}</span>
              </div>
              <div class="card-subtitle">
                EVOLUCIÓN DE CAMPAÑAS <span class="muted">Mensuales</span>
              </div>
            </div>
          </div>

          <div style="height: 400px;">
            <BarChart :chart-data="campaignsData" />
          </div>
        </v-card>
      </v-col>

      <!-- CARD FOLLETOS -->
      <v-col cols="12" md="6">
        <v-card class="campaign-card">
          <div class="card-header">
            <div class="left">
              <div class="title">FOLLETOS</div>
            </div>

            <div class="right">
              <div class="total-line">
                <span class="total-label">TOTAL REPARTIDOS</span>
                <span class="total-value">{{ kpis[1].value }}</span>
              </div>
              <div class="card-subtitle">
                FOLLETOS REPARTIDOS <span class="muted">Mensual</span>
              </div>
            </div>
          </div>

          <div style="height: 400px;">
            <BarChart :chart-data="folletosData" />
          </div>
        </v-card>
      </v-col>

    </v-row>

    <!-- MAPA -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card class="campaign-card">

          <div class="card-header">
            <div class="left">
              <div class="title">DISTRIBUCIÓN POR ZONAS (Top 15)</div>
            </div>
          </div>

          <!-- ARA EL CONTINGUT EN DUES COLUMNES -->
          <v-row class="pa-3">

            <!-- MAPA (ESQUERRA) -->
            <v-col cols="12" md="8">
              <div id="province-map" style="height: 520px; width: 100%;"></div>
            </v-col>

            <!-- TAULA / LATERAL (DRETA) -->
            <v-col cols="12" md="4">
              <v-card class="pa-3" elevation="0" style="background: transparent;">
                <h3 class="mb-4">Resumen por provincia (Top 15)</h3>
                <v-table density="compact">
                  <thead>
                    <tr>
                      <th>Provincia</th>
                      <th class="text-right">Folletos</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in topProvinces" :key="p.state_id">
                      <td>{{ p.state_name }}</td>
                      <td class="text-right">{{ p.quantity.toLocaleString('es-ES') }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </v-card>
            </v-col>

          </v-row>

        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :timeout="6000" color="error" variant="tonal">
      {{ snackbar.msg }}
    </v-snackbar>

  </v-container>
</template>


<script setup>
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'

import BarChart from '../../components/charts/BarChart.vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png'
import markerIconUrl from 'leaflet/dist/images/marker-icon.png'
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png'

const defaultPinIcon = L.icon({
  iconRetinaUrl: markerIcon2xUrl,
  iconUrl: markerIconUrl,
  shadowUrl: markerShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
})

/* ---------- Estat ---------- */
const clientSession = ref({})
const ClientId = ref(null)
const IdBotiga = ref(null)
const loading = ref(true)
const snackbar = ref({ show: false, msg: '' })

/* ---------- Top províncies ---------- */
const topProvinces = ref([])
const loadingTopProvinces = ref(true)
const topYear = new Date().getFullYear()

/* ---------- Mapa ---------- */
const loadingProvinceMap = ref(true)
let map = null
let geoLayer = null
let markersLayer = null

function getColor(v) {
  return v > 5000000 ? '#08306b' :
         v > 1000000 ? '#08519c' :
         v > 500000  ? '#2171b5' :
         v > 200000  ? '#4292c6' :
         v > 100000  ? '#6baed6' :
         v > 50000   ? '#9ecae1' :
         v > 10000   ? '#c6dbef' :
                       '#deebf7';
}

/* ---------- API ---------- */
async function fetchTopProvincesYear(clientId, { year, limit = 10 } = {}) {
  return axios.post('/api/estadistiques/top-provinces-year', { ClientId: clientId, IdBotiga: IdBotiga.value, year, limit })
}

/* ---------- Top i mapa ---------- */
async function loadTopProvinces() {
  loadingTopProvinces.value = true
  try {
    const resp = await fetchTopProvincesYear(ClientId.value, { year: topYear, limit: 15 })
    topProvinces.value = resp.data?.provinces ?? []

    await renderProvinceMap()
  } catch (e) {
    console.error('[TopProvinces] error:', e)
    snackbar.value = { show: true, msg: 'No s’han pogut carregar les províncies.' }
  } finally {
    loadingTopProvinces.value = false
  }
}

async function renderProvinceMap() {
  loadingProvinceMap.value = true
  try {
    await nextTick()
    const el = document.getElementById('province-map')
    if (!el) return

    if (!map) {
      map = L.map('province-map', { zoomControl: true }).setView([40.2, -3.7], 6)
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap & CARTO',
        subdomains: 'abcd',
        maxZoom: 20,
      }).addTo(map)
    }

    // 🔄 Neteja capes anteriors
    if (geoLayer) { geoLayer.remove(); geoLayer = null }
    if (markersLayer) { markersLayer.clearLayers(); map.removeLayer(markersLayer); markersLayer = null }

    // 🔹 Carrega GeoJSON complet (50 províncies)
    const geoResp = await axios.get('/geo/spain-provinces.geojson')
    const geoData = geoResp.data

    // 🔸 Llista de noms del top 10
    const topNames = topProvinces.value.map(p =>
      normalizeProvinceName(p.state_name)
    )

    const totals = Object.fromEntries(
      topProvinces.value.map(p => [
        normalizeProvinceName(p.state_name),
        p.quantity
      ])
    )

    // 🔹 Filtra només les províncies del top 10
    const filteredFeatures = geoData.features.filter(f =>
      topNames.includes(normalizeProvinceName(f.properties.name))
    )


    const filteredGeo = { ...geoData, features: filteredFeatures }

    // 🔹 Capa choroplètica només per al Top 10
    geoLayer = L.geoJSON(filteredGeo, {
      style: f => {
        const nom = normalizeProvinceName(f.properties.name)
        const tot = totals[nom] ?? 0
        return {
          fillColor: getColor(tot),
          weight: 1,
          color: '#333',
          opacity: 1,
          fillOpacity: 0.75,
        }
      },
      onEachFeature: (feature, layer) => {
        const nom = feature?.properties?.name?.trim()
        const tot = totals[normalizeProvinceName(feature.properties.name)] ?? 0
        layer.bindTooltip(
          `<b>${nom}</b><br>${tot.toLocaleString('es-ES')} folletos`,
          { sticky: true }
        )
      },
    }).addTo(map)

    // 🔹 Centrar només sobre les top 10
    const bounds = geoLayer.getBounds()
    if (bounds?.isValid()) map.fitBounds(bounds, { padding: [20, 20] })
  } catch (e) {
    console.error('[ProvinceMap] error:', e)
    snackbar.value = { show: true, msg: 'Error pintant el mapa de províncies (top).' }
  } finally {
    loadingProvinceMap.value = false
  }
}

function normalizeProvinceName(name) {
  return name
    .replace(/\(.*?\)/g, '')  // elimina tot el que hi ha entre parèntesi
    .trim()
    .toLowerCase();
}



// ESTADÍSTIQUES/GRÀFIQUES CAMPANYES I FOLLETOS

const kpis = ref([
  { label: 'Campañas', value: 0 },
  { label: 'Folletos repartidos', value: 0 }, 
])

/* ---------- Datasets per a columnes ---------- */
const campaignsData = ref({ labels: [], datasets: [] })
const folletosData  = ref({ labels: [], datasets: [] })

/* ---------- Funcions ---------- */
function loadClient() {
  const data = localStorage.getItem('clientSession')
  if (!data) return
  clientSession.value = JSON.parse(data)
  ClientId.value = clientSession.value.client_id
  IdBotiga.value = clientSession.value.IdBotiga || clientSession.value.IdBotiga || null
}

async function fetchFlyers(clientId) {
  return axios.post('/api/estadistiques/flyers', { ClientId: clientId, IdBotiga: IdBotiga.value })
}
async function fetchCampaignsByGroup(clientId) {
  return axios.post('/api/estadistiques/CountCampaignsByGroup', { ClientId: clientId, IdBotiga: IdBotiga.value })
}


/* ---------- Carrega gràfiques ---------- */
async function loadCharts() {
  const [flyersResp, campaignsResp] = await Promise.all([
    fetchFlyers(ClientId.value),
    fetchCampaignsByGroup(ClientId.value),
  ])

  const flyers = flyersResp.data?.months ?? []
  const campaigns = campaignsResp.data?.months ?? []

  const allMonths = [...new Set([
    ...flyers.map(f => f.month),
    ...campaigns.map(c => c.month)
  ])].sort()

  const labels = allMonths.map(m =>
    new Intl.DateTimeFormat('es-ES', { month: 'short' })
      .format(new Date(m + '-01'))
      .replace('.', '')
      .replace(/^\w/, c => c.toUpperCase()) + '.'
  )

  const flyerValues = allMonths.map(m =>
    flyers.find(f => f.month === m)?.given_coverage ?? 0
  )

  const campaignCounts = allMonths.map(m =>
    campaigns.find(c => c.month === m)?.total ?? 0
  )

  /* --- SET CAMPAÑAS --- */
  campaignsData.value = {
    labels,
    datasets: [
      {
        label: 'Campañas',
        data: campaignCounts,
        backgroundColor: '#08c0e6',
        borderRadius: 8,
        borderSkipped: false,
        barPercentage: 0.55,
        categoryPercentage: 0.8
      }
    ]
  }

  /* --- SET FOLLETOS --- */
  folletosData.value = {
    labels,
    datasets: [
      {
        label: 'Folletos',
        data: flyerValues,
        backgroundColor: '#08c0e6',
        borderRadius: 8,
        borderSkipped: false,
        barPercentage: 0.55,
        categoryPercentage: 0.8
      }
    ]
  }

  /* --- KPIs --- */
  kpis.value[0].value = campaignCounts.reduce((a, b) => a + b, 0).toLocaleString('es-ES')
  kpis.value[1].value = flyerValues.reduce((a, b) => a + b, 0).toLocaleString('es-ES')
}


/* ---------- Init ---------- */
async function init() {
  try {
    loadClient()
    if (!ClientId.value) throw new Error('ClientId no trobat a localStorage')
    await Promise.all([loadCharts(), loadTopProvinces()])
  } catch (e) {
    snackbar.value = { show: true, msg: e.message || 'Error carregant estadístiques.' }
  } finally {
    loading.value = false
  }
}
onMounted(() => init())
</script>



<style>
#province-map { height: 460px; width: 100%; position: relative; z-index: 1; }
.leaflet-pane, .leaflet-top, .leaflet-bottom { z-index: 2; }



/* Fila superior */
.card-header{
  display:flex;
  padding: 20px;
  gap:16px;
  align-items:flex-start;
  flex-wrap:wrap;
}
.left .title{font-weight:900;color:#e6edf3;letter-spacing:.4px; font-size: 20px;}

/* Píndola de l’any */
.year-pill{
  border-color:#3a4a57 !important;
  background:#0e151c !important;
  color:#e6edf3 !important;
}

/* Bloc dret */
.right{margin-left:auto;display:flex;flex-direction:column;align-items:flex-end}
.total-line{display:flex;gap:12px;align-items:baseline}
.total-label{font-weight:800;color:#e6edf3}
.total-value{color:#08c0e6;font-weight:900;font-size:1.15rem}
.card-subtitle{margin-top:6px;font-weight:800;color:#e6edf3; margin-top:18px; font-size: 20px;}
.card-subtitle .muted{color:#cfd8dc;font-weight:700; margin-top:14px}

</style>
